import React, { useState, useEffect, useMemo } from 'react';
import { doc, setDoc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useChildren } from '../context/ChildProvider';
import { stimulationActivities } from '../helpers/stimulationActivities';
import { calculateAgeMonths } from '../helpers/utils';
import { Spinner, Button } from '../components/UI';

const ModernActivityItem = ({ activity, isDone, onToggle, priority = 'normal' }) => {
    const getPriorityStyle = () => {
        switch (priority) {
            case 'high':
                return {
                    border: 'border-red-300 hover:border-red-400',
                    bg: 'bg-red-50/80',
                    icon: '🚨',
                    label: 'Prioritas Tinggi',
                    color: 'red'
                };
            case 'medium':
                return {
                    border: 'border-amber-300 hover:border-amber-400',
                    bg: 'bg-amber-50/80',
                    icon: '⚠️',
                    label: 'Perhatian',
                    color: 'amber'
                };
            default:
                return {
                    border: 'border-white/50 hover:border-white/80',
                    bg: 'bg-white/40',
                    icon: '✨',
                    label: 'Rutin',
                    color: 'indigo'
                };
        }
    };

    const style = getPriorityStyle();

    return (
        <div 
            onClick={() => onToggle(activity.id, !isDone)} 
            className={`
                group flex items-start space-x-4 p-4 rounded-[1.25rem] cursor-pointer border backdrop-blur-md transition-all duration-300
                ${style.border} ${style.bg} hover:shadow-lg transform hover:-translate-y-0.5 active:scale-95
                ${isDone ? 'opacity-50 grayscale' : ''}
            `}
        >
            <div className={`
                mt-0.5 flex-shrink-0 w-8 h-8 rounded-[0.75rem] border-2 flex items-center justify-center transition-all duration-300
                ${isDone 
                    ? 'bg-gradient-to-r from-emerald-400 to-emerald-500 border-emerald-400 shadow-md' 
                    : `border-${style.color}-300 bg-white/50 group-hover:bg-white`
                }
            `}>
                {isDone ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                ) : (
                    <span className="text-xs">{style.icon}</span>
                )}
            </div>
            <div className="flex-1">
                <p className={`font-bold text-[13px] text-gray-800 leading-snug ${isDone ? 'line-through text-gray-500' : ''}`}>
                    {activity.text}
                </p>
                {priority !== 'normal' && !isDone && (
                    <div className="mt-1.5">
                        <span className={`
                            inline-flex items-center px-2 py-0.5 rounded-lg text-[9px] font-bold uppercase tracking-wider
                            ${priority === 'high' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}
                        `}>
                            {style.label}
                        </span>
                    </div>
                )}
                {activity.description && (
                    <p className={`text-[10px] font-medium mt-1 leading-tight ${isDone ? 'text-gray-400' : 'text-gray-500'}`}>{activity.description}</p>
                )}
            </div>
        </div>
    );
};

const StimulationCategory = ({ title, activities, stimulationProgress, onToggle, icon, color }) => (
    <div className="relative overflow-hidden rounded-[1.5rem] p-5 border border-white/60 bg-white/40 backdrop-blur-xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] mb-4">
        <div className={`absolute -right-10 -top-10 w-32 h-32 bg-${color}-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30`}></div>
        <div className={`absolute -left-10 -bottom-10 w-32 h-32 bg-${color}-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30`}></div>
        
        <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-5">
                <div className={`w-10 h-10 rounded-[1rem] bg-${color}-100 text-${color}-600 flex items-center justify-center text-xl shadow-sm border border-${color}-200`}>
                    {icon}
                </div>
                <h3 className="font-extrabold text-gray-800 text-[15px] tracking-tight">{title}</h3>
            </div>
            <div className="space-y-3">
                {activities.map(activity => (
                    <ModernActivityItem 
                        key={activity.id} 
                        activity={activity} 
                        isDone={!!stimulationProgress.activities?.[activity.id]} 
                        onToggle={onToggle}
                        priority={activity.priority || 'normal'}
                    />
                ))}
            </div>
        </div>
    </div>
);

export default function StimulationScreen({ user, navigate }) {
    const { myChildren: children, loading } = useChildren();
    const [selectedChild, setSelectedChild] = useState(null);
    const [stimulationProgress, setStimulationProgress] = useState({});
    const [lastAssessment, setLastAssessment] = useState(null);

    useEffect(() => {
        if (!selectedChild && children.length > 0) {
            setSelectedChild(children[0]);
        }
    }, [children, selectedChild]);

    useEffect(() => {
        if (!selectedChild) return;
        
        const fetchData = async () => {
            try {
                const ownerUid = selectedChild.ownerUid || user.uid;
                const progressRef = doc(db, `users/${ownerUid}/children/${selectedChild.id}/privateData/stimulation`);
                const progressSnap = await getDoc(progressRef);
                if (progressSnap.exists()) {
                    setStimulationProgress(progressSnap.data());
                } else {
                    setStimulationProgress({});
                }

                const assessmentsRef = collection(db, `users/${ownerUid}/children/${selectedChild.id}/assessments`);
                const assessmentQuery = query(assessmentsRef, orderBy('createdAt', 'desc'), limit(1));
                const assessmentSnap = await getDocs(assessmentQuery);
                
                if (!assessmentSnap.empty) {
                    setLastAssessment(assessmentSnap.docs[0].data());
                } else {
                    setLastAssessment(null);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        
        fetchData();
    }, [selectedChild, user.uid]);

    const handleToggleActivity = async (activityId, isDone) => {
        const today = new Date().toISOString().split('T')[0];
        const lastStreakDate = stimulationProgress.lastStreakDate;
        const currentStreak = stimulationProgress.streak || 0;
        let newStreak = currentStreak;

        if (isDone) {
            if (lastStreakDate !== today) {
                const yesterday = new Date(Date.now() - 864e5).toISOString().split('T')[0];
                newStreak = lastStreakDate === yesterday ? currentStreak + 1 : 1;
            }
        }
        
        const newProgress = {
            ...stimulationProgress,
            activities: {
                ...(stimulationProgress.activities || {}),
                [activityId]: isDone
            },
            streak: newStreak,
            lastStreakDate: isDone ? today : lastStreakDate
        };
        setStimulationProgress(newProgress);

        try {
            const ownerUid = selectedChild.ownerUid || user.uid;
            const progressRef = doc(db, `users/${ownerUid}/children/${selectedChild.id}/privateData/stimulation`);
            await setDoc(progressRef, newProgress);
        } catch (error) {
            console.error("Error saving progress:", error);
        }
    };
    
    const handleChildChange = (e) => {
        const child = children.find(c => c.id === e.target.value);
        setSelectedChild(child);
    };

    const generateStimulationActivities = useMemo(() => {
        if (!selectedChild) return { ageBasedActivities: [], assessmentBasedActivities: [] };
        const ageMonths = calculateAgeMonths(selectedChild.dob.toDate());
        const ageBasedGroup = stimulationActivities.find(group => ageMonths >= group.ageRange[0] && ageMonths <= group.ageRange[1]);
        
        let assessmentBasedActivities = [];
        if (lastAssessment && lastAssessment.perkembangan) {
            const kpspAnswers = lastAssessment.perkembangan.answers || {};
            const kpspQuestions = lastAssessment.perkembangan.questions || [];
            const areasNeedingImprovement = [];
            Object.entries(kpspAnswers).forEach(([index, answer]) => {
                if (answer === 'Tidak' && kpspQuestions[index]) {
                    areasNeedingImprovement.push({
                        domain: kpspQuestions[index].domain,
                        question: kpspQuestions[index].q,
                        priority: 'high'
                    });
                }
            });
            
            areasNeedingImprovement.forEach((area, index) => {
                const domainActivities = {
                    'Gerak Kasar': [
                        { id: `targeted_gross_${index}`, text: `Latihan gerak kasar: ${area.question.toLowerCase()}`, priority: 'high', description: 'Berdasarkan hasil KPSP' },
                    ],
                    'Gerak Halus': [
                        { id: `targeted_fine_${index}`, text: `Latihan gerak halus: ${area.question.toLowerCase()}`, priority: 'high', description: 'Berdasarkan hasil KPSP' },
                    ],
                    'Bicara & Bahasa': [
                        { id: `targeted_speech_${index}`, text: `Latihan bicara: ${area.question.toLowerCase()}`, priority: 'high', description: 'Berdasarkan hasil KPSP' },
                    ],
                    'Sosialisasi & Kemandirian': [
                        { id: `targeted_social_${index}`, text: `Latihan sosialisasi: ${area.question.toLowerCase()}`, priority: 'high', description: 'Berdasarkan hasil KPSP' },
                    ]
                };
                if (domainActivities[area.domain]) assessmentBasedActivities.push(...domainActivities[area.domain]);
            });
        }
        return {
            ageBasedActivities: ageBasedGroup?.activities || [],
            assessmentBasedActivities
        };
    }, [selectedChild, lastAssessment]);

    const progressPercentage = useMemo(() => {
        const allActivities = [...generateStimulationActivities.ageBasedActivities, ...generateStimulationActivities.assessmentBasedActivities];
        if (allActivities.length === 0) return 0;
        const completedActivities = allActivities.filter(act => stimulationProgress.activities?.[act.id]).length;
        return Math.round((completedActivities / allActivities.length) * 100);
    }, [generateStimulationActivities, stimulationProgress]);

    if (loading) return <Spinner />;
    
    if (children.length === 0) {
        return (
            <div className="min-h-screen bg-[#F8FAFC] flex flex-col pt-20 px-5">
                <div className="text-center py-12 px-6 bg-white/50 backdrop-blur-xl rounded-[2.5rem] border border-white/60 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                    <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-5xl mx-auto mb-6 shadow-sm">👶</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2 tracking-tight">Belum Ada Data Anak</h3>
                    <p className="text-sm text-gray-500 mb-8 max-w-xs mx-auto leading-relaxed">
                        Silakan tambah data anak di Beranda untuk mulai stimulasi.
                    </p>
                    <Button onClick={() => navigate('home')} className="bg-gray-900 text-white hover:bg-gray-800 shadow-xl w-full py-4 rounded-2xl">
                        🏠 Kembali ke Beranda
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-emerald-200 pb-28 pt-10 px-5 relative">
            {/* Animated Background Mesh */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[10%] left-[-20%] w-[60vw] h-[60vw] bg-emerald-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute top-[40%] right-[-20%] w-[50vw] h-[50vw] bg-teal-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-[-10%] left-[10%] w-[70vw] h-[70vw] bg-cyan-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-4000"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
            </div>

            <div className="relative z-10 max-w-md mx-auto space-y-6">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-white/60 backdrop-blur-md rounded-[1.25rem] flex items-center justify-center text-3xl mx-auto mb-4 shadow-sm border border-white">
                        🎯
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">Pusat Stimulasi</h2>
                    <p className="text-sm text-gray-500 mt-2">Aktivitas stimulasi yang dipersonalisasi</p>
                </div>

                <div className="bg-white/60 backdrop-blur-xl rounded-[1.5rem] p-5 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Pilih Anak</label>
                    <select 
                        value={selectedChild?.id || ''} 
                        onChange={handleChildChange} 
                        className="w-full bg-white/80 border border-gray-200 text-gray-800 text-sm font-bold rounded-xl focus:ring-emerald-500 focus:border-emerald-500 block p-3 shadow-sm outline-none"
                    >
                        {children.map(child => (
                            <option key={child.id} value={child.id}>
                                {child.name} ({calculateAgeMonths(child.dob.toDate())} bln)
                            </option>
                        ))}
                    </select>
                </div>

                {selectedChild && (
                    <div className="grid grid-cols-2 gap-4">
                        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-400 to-teal-500 rounded-[1.5rem] p-4 text-white shadow-lg border border-emerald-300">
                            <div className="absolute -right-4 -top-4 w-16 h-16 bg-white rounded-full mix-blend-overlay opacity-20"></div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-2xl drop-shadow-sm">📊</span>
                                <span className="text-2xl font-extrabold drop-shadow-sm">{progressPercentage}%</span>
                            </div>
                            <h4 className="font-bold text-[11px] uppercase tracking-wider opacity-90">Progres Hari Ini</h4>
                            <div className="w-full bg-white/30 rounded-full h-1.5 mt-2 overflow-hidden">
                                <div className="bg-white h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${progressPercentage}%` }}></div>
                            </div>
                        </div>
                        <div className="relative overflow-hidden bg-gradient-to-br from-orange-400 to-amber-500 rounded-[1.5rem] p-4 text-white shadow-lg border border-orange-300">
                            <div className="absolute -left-4 -bottom-4 w-20 h-20 bg-white rounded-full mix-blend-overlay opacity-20"></div>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-2xl drop-shadow-sm">🔥</span>
                                <span className="text-2xl font-extrabold drop-shadow-sm">{stimulationProgress.streak || 0}</span>
                            </div>
                            <h4 className="font-bold text-[11px] uppercase tracking-wider opacity-90">Streak Hari</h4>
                            <p className="text-[9px] mt-1 font-medium opacity-80">Konsistensi stimulasi</p>
                        </div>
                    </div>
                )}

                <div className="mt-6 space-y-4">
                    {generateStimulationActivities.assessmentBasedActivities.length > 0 && (
                        <StimulationCategory
                            title="Berdasarkan Skrining"
                            activities={generateStimulationActivities.assessmentBasedActivities}
                            stimulationProgress={stimulationProgress}
                            onToggle={handleToggleActivity}
                            icon="🚨"
                            color="rose"
                        />
                    )}

                    {generateStimulationActivities.ageBasedActivities.length > 0 && (
                        <StimulationCategory
                            title={`Sesuai Usia (${calculateAgeMonths(selectedChild?.dob.toDate())} bln)`}
                            activities={generateStimulationActivities.ageBasedActivities}
                            stimulationProgress={stimulationProgress}
                            onToggle={handleToggleActivity}
                            icon="🧠"
                            color="indigo"
                        />
                    )}
                </div>

                {generateStimulationActivities.ageBasedActivities.length === 0 && 
                 generateStimulationActivities.assessmentBasedActivities.length === 0 && (
                    <div className="text-center py-12 bg-white/50 backdrop-blur-xl rounded-[2.5rem] border border-white/60 shadow-sm">
                        <div className="text-5xl mb-4">🧩</div>
                        <h3 className="text-lg font-bold text-gray-800 mb-2 tracking-tight">Belum Ada Aktivitas</h3>
                        <p className="text-xs text-gray-500 mb-6 px-4">Lakukan assessment terlebih dahulu untuk mendapatkan rekomendasi.</p>
                        <Button onClick={() => navigate('assessmentHub')} className="bg-gray-900 text-white rounded-xl">
                            🔍 Mulai Assessment
                        </Button>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
            `}</style>
        </div>
    );
}
